import React, { useState, useEffect } from 'react';
import { Profile, Transaction } from '../types';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  jwt: string | null;
  profile: Profile;
  transactions: Transaction[];
  authStatus: string;
  isLoggingIn: boolean;
  isSaving: boolean;
  onLogin: () => void;
  onLogout: () => void;
  onReload: () => void;
  onSave: () => void;
  onProfileChange: (updates: Partial<Profile>) => void;
  onOpenWalletSetup: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  jwt,
  profile,
  transactions,
  authStatus,
  isLoggingIn,
  isSaving,
  onLogin,
  onLogout,
  onReload,
  onSave,
  onProfileChange,
  onOpenWalletSetup
}) => {
  const [authTimeout, setAuthTimeout] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isLoggingIn) {
      setAuthTimeout(false);
      timer = setTimeout(() => {
        setAuthTimeout(true);
      }, 15000); // 15 sec
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoggingIn]);

  if (!isOpen) return null;

  const allRewardWalletsPresent = Boolean(profile.xch_wallet) && Boolean(profile.fil_wallet) && Boolean(profile.ar_wallet);
  const totalXCH = transactions
    .filter(tx => tx.chain?.toLowerCase() === 'xch' && tx.direction === 'in')
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  const totalFIL = transactions
    .filter(tx => tx.chain?.toLowerCase() === 'fil' && tx.direction === 'in')
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  const totalAR = transactions
    .filter(tx => tx.chain?.toLowerCase() === 'ar' && tx.direction === 'in')
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-md border border-gray-700/50 rounded-2xl shadow-2xl">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700/50 z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg">
              <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 
                           bg-clip-text text-transparent">
              Account Overview
            </h3>
          </div>
          
          {!jwt ? (
            <div className="text-center space-y-4">
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 mb-4">
                <p className="text-gray-300 text-sm mb-2">
                  Sign in with your wallet to access staking features
                </p>
                <p className="text-gray-400 text-xs">
                  This creates a secure session for your staking dashboard
                </p>
              </div>
              
              <button
                onClick={onLogin}
                disabled={isLoggingIn && !authTimeout}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700
                           text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300
                           transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/25
                           disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                           flex items-center justify-center gap-3"
              >
                {isLoggingIn && !authTimeout ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Authenticating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign In / Create Account</span>
                  </>
                )}
              </button>

              {authTimeout && (
                <div className="mt-3 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg">
                  It looks like authentication is taking too long.<br />
                  Please log in to MetaMask first, then click 
                  <strong> Sign In / Create Account </strong> again.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Session Status */}
              <div className="flex items-center justify-between p-3 bg-emerald-900/30 
                              border border-emerald-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-emerald-300 font-medium">Logged in</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={onReload}
                    className="p-2 text-gray-400 hover:text-cyan-400 transition-colors rounded-lg
                               hover:bg-gray-700/50"
                    title="Refresh Profile"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button
                    onClick={onLogout}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors rounded-lg
                               hover:bg-gray-700/50"
                    title="Logout"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Account Settings */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Account Settings
                </h4>

                {/* Email Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profile.email ?? ""}
                    onChange={(e) => onProfileChange({ email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg px-4 py-3 
                               text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                               focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-200
                               hover:border-gray-500/50"
                  />
                </div>

                {/* XCH Wallet Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Chia (XCH) Wallet Address
                  </label>
                  <input
                    type="text"
                    value={profile.xch_wallet ?? ""}
                    onChange={(e) => onProfileChange({ xch_wallet: e.target.value })}
                    placeholder="xch1..."
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg px-4 py-3 
                               text-white placeholder-gray-400 font-mono text-sm focus:outline-none 
                               focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 
                               transition-all duration-200 hover:border-gray-500/50"
                  />
                </div>

                {/* FIL Wallet Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Filecoin (FIL) Wallet Address
                  </label>
                  <input
                    type="text"
                    value={profile.fil_wallet ?? ""}
                    onChange={(e) => onProfileChange({ fil_wallet: e.target.value })}
                    placeholder="f1..."
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg px-4 py-3 
                               text-white placeholder-gray-400 font-mono text-sm focus:outline-none 
                               focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 
                               transition-all duration-200 hover:border-gray-500/50"
                  />
                </div>

                {/* AR Wallet Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <img
                      src="/arweave.png"
                      alt="Arweave (AR) logo"
                      className="w-4 h-4 rounded-full object-cover ring-1 ring-sky-400/60 bg-white/5"
                    />
                    Arweave (AR) Wallet Address
                  </label>
                  <input
                    type="text"
                    value={profile.ar_wallet ?? ""}
                    onChange={(e) => onProfileChange({ ar_wallet: e.target.value })}
                    placeholder="43-character Arweave address"
                    className="w-full bg-gray-800/50 border border-gray-600/50 rounded-lg px-4 py-3
                               text-white placeholder-gray-400 font-mono text-sm focus:outline-none
                               focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500/50
                               transition-all duration-200 hover:border-gray-500/50"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={onSave}
                  disabled={isSaving}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 
                             hover:from-emerald-600 hover:to-green-700 text-white font-semibold 
                             px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 
                             hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 
                             disabled:cursor-not-allowed disabled:transform-none
                             flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      <span>Save Profile</span>
                    </>
                  )}
                </button>
                
                {!allRewardWalletsPresent && (
                  <button
                    onClick={onOpenWalletSetup}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 
                               hover:from-amber-600 hover:to-orange-700 text-white font-semibold 
                               px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 
                               hover:shadow-lg hover:shadow-amber-500/25 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                    <span>Setup XCH/FIL/AR Wallets</span>
                  </button>
                )}
              </div>

              {/* Wallet Status Indicator */}
              {jwt && (
                <div className="mt-4 p-3 bg-gray-800/30 border border-gray-700/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Wallet Configuration:</span>
                    <div className="flex items-center gap-2">
                      {allRewardWalletsPresent ? (
                        <>
                          <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                          <span className="text-sm text-emerald-400 font-medium">Complete</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                          <span className="text-sm text-amber-400 font-medium">Setup Required</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 flex gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full ${profile.xch_wallet ? "bg-green-400" : "bg-gray-500"}`} />
                      <span className="text-gray-400">XCH Wallet</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full ${profile.fil_wallet ? "bg-purple-400" : "bg-gray-500"}`} />
                      <span className="text-gray-400">FIL Wallet</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full ${profile.ar_wallet ? "bg-sky-400" : "bg-gray-500"}`} />
                      <span className="text-gray-400">AR Wallet</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Earnings Section */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.1 0-2 .9-2 2h4c0-1.1-.9-2-2-2zm-6 4h12m-6 4c1.1 0 2-.9 2-2H8c0 1.1.9 2 2 2z"
                    />
                  </svg>
                  Earnings
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                    <p className="text-sm text-gray-400">Total earned (XCH)</p>
                    <p className="text-xl font-bold text-emerald-400">{totalXCH}</p>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                    <p className="text-sm text-gray-400">Total earned (FIL)</p>
                    <p className="text-xl font-bold text-purple-400">{totalFIL}</p>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                    <p className="text-sm text-gray-400">Total earned (AR)</p>
                    <p className="text-xl font-bold text-sky-400">{totalAR}</p>
                  </div>
                </div>

                {/* Transaction History */}
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-2">Transaction History</p>
                  <div className="bg-gray-800/30 border border-gray-700/30 rounded-lg p-3 text-sm text-gray-300 max-h-60 overflow-y-auto">
                    {transactions.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">No transactions yet.</p>
                    ) : (
                      <ul className="divide-y divide-gray-700/50">
                        {transactions.map((tx, i) => (
                          <li key={i} className="py-2 flex items-center justify-between">
                            <span
                              className={
                                tx.direction === "in"
                                  ? "text-emerald-400 font-medium"
                                  : "text-red-400 font-medium"
                              }
                            >
                              {tx.direction.toUpperCase()} {tx.amount} {tx.chain.toUpperCase()}
                            </span>

                            <span className="text-gray-400 flex items-center gap-3">
                              {new Date(tx.tx_timestamp).toLocaleString()}
                              {tx.tx_hash && (
                                <a
                                  href={
                                    tx.chain === "xch"
                                      ? `https://xch.dwd.com/info/coin/${tx.tx_hash}`
                                      : tx.chain === "fil"
                                      ? `https://filscan.io/en/message/${tx.tx_hash}`
                                      : tx.chain === "ar"
                                      ? `https://viewblock.io/arweave/tx/${tx.tx_hash}`
                                      : `https://bscscan.com/tx/${tx.tx_hash}`
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-cyan-400 hover:underline"
                                >
                                  View
                                </a>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Message */}
          {authStatus && (
            <div className="mt-4 p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg">
              <p className="text-sm text-gray-300 flex items-center gap-2">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                {authStatus}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
